import { capitalizeWords, formatClockTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { StaffLessonNow } from '@/types/staff';

export interface LessonNowNextProps {
  now: StaffLessonNow | null | undefined;
  next: StaffLessonNow | null | undefined;
  /** Whether they teach at all, so "no more classes" is said only of a teacher. */
  teaches: boolean;
}

/**
 * Where a member of staff is this period and where they go next, today, from
 * the published timetable. The staff register's and the attendance day's
 * "Now / Next" cell — one component so the two never read differently.
 */
export function LessonNowNext({ now, next, teaches }: LessonNowNextProps) {
  if (!now && !next) {
    return (
      <span className="text-xs font-semibold text-slate-400">
        {teaches ? 'No more classes today' : '—'}
      </span>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {now && <LessonLine label="Now" tone="now" lesson={now} />}
      {next && <LessonLine label="Next" tone="next" lesson={next} />}
    </div>
  );
}

/** One "Now" or "Next" line: the class, the subject and the bell times. */
function LessonLine({
  label,
  tone,
  lesson,
}: {
  label: string;
  tone: 'now' | 'next';
  lesson: StaffLessonNow;
}) {
  return (
    <div className="flex items-start gap-1.5">
      <span
        className={cn(
          'mt-0.5 shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wider border',
          tone === 'now'
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800'
            : 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
        )}
      >
        {label}
      </span>
      <div className="min-w-0 leading-tight">
        <p className="truncate text-xs font-bold text-slate-800 dark:text-slate-100">
          {lesson.displayName} · {capitalizeWords(lesson.subject)}
        </p>
        <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400 tabular-nums">
          {formatClockTime(lesson.startTime)} – {formatClockTime(lesson.endTime)}
        </p>
      </div>
    </div>
  );
}
